import {isEqual} from 'lodash';
import promiseRetry from 'promise-retry';
import {Fragment, PureComponent} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';

import {KurentoRoomProvider, GroupRoomManager} from './shared/ConferenceRoom';
import {InformationModal, Loader} from 'components';
import {BROWSER_SUPPORT_ERROR} from 'constants/webrtc';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {MediaDevicesConstraints, WebRTCConnectDetails} from 'types';
import {browserUtils, logger, mediaDevicesUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.WEBRTC;

type ChildrenProps = {
    room: GroupRoomManager;
}

type Props = {
    mediaConstraints?: MediaDevicesConstraints;
    ignoreErrorDialog?: boolean;
    onEnumerateMediaDevices?: (devices: MediaDeviceInfo[]) => void;
    onEnumerateTracks?: (tracks: MediaStreamTrack[]) => void;
    onWebRTCStartSuccess?: (details?: WebRTCConnectDetails) => void;
    onWebRTCStartFailed?: (details: WebRTCConnectDetails) => void;
    getTokenFn: () => Promise<{token: string}>;
    onDisconnect?: () => void;
    children: (data: ChildrenProps) => React.ReactElement;
} & WithTranslation;

type State = {
    room: GroupRoomManager;
    token: string;
    errorDialogProps: {
        open: boolean;
        message: string;
    };
    failedConnectDetails: WebRTCConnectDetails;
};

const initialState: State = {
    room: null,
    token: null,
    errorDialogProps: {
        open: false,
        message: ''
    },
    failedConnectDetails: null,
};

class KurentoRoom extends PureComponent<Props, Readonly<State>> {
    readonly state = initialState;
    _isMounted = false;

    get isMediaDevicesEnumerated(): boolean {
        return !!this.props.mediaConstraints?.audioinput || !!this.props.mediaConstraints?.videoinput;
    }

    async componentDidMount() {
        this._isMounted = true;
        window.addEventListener('beforeunload', this.disconnectRoom);
        window.addEventListener('pagehide', this.disconnectRoom);

        try {
            this.checkPlatform();
            if (this.isMediaDevicesEnumerated) {
                await this.getUserMedia();
            }
            this.start();
        } catch (error: any) {
            logger.warn({category: logger.category.WebRTC, message: 'Room connection failed'}, error.message);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (!isEqual(prevProps.mediaConstraints, this.props.mediaConstraints) && this.isMediaDevicesEnumerated) {
            // getUserMedia logs the failure and raises the error dialog itself, so the rejection is only swallowed
            // here — unhandled, it reaches the console as "Uncaught (in promise)" on every activity switch
            void this.getUserMedia().catch(() => null);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.props.onEnumerateMediaDevices?.([]);
        this.props.onEnumerateTracks?.([]);
        this.disconnectRoom();
        window.removeEventListener('beforeunload', this.disconnectRoom);
        window.removeEventListener('pagehide', this.disconnectRoom);
    }

    checkPlatform = () => {
        if (!browserUtils.isWebRTCSupported()) {
            const message = 'Browser does not support required capabilities';
            logger.warn({category: logger.category.WebRTC, message});
            this.webRTCStartFailed({
                code: BROWSER_SUPPORT_ERROR,
                message,
                attempt: 1
            }, T_KEYS.ROOM_ERROR_DIALOG.NOT_SUPPORTED);
            throw new Error(message);
        }
    };

    getUserMedia = async () => {
        try {
            const {audioinput, videoinput} = this.props.mediaConstraints || {};
            // Camera is requested only when the running activity needs video, otherwise the browser shows a
            // combined microphone + camera permission prompt for audio-only activities
            const enumerateDevices = {
                ...audioinput && {audio: audioinput},
                ...videoinput && {
                    video: {
                        width: {ideal: 960},
                        height: {ideal: 720},
                        ...videoinput,
                    } as MediaTrackConstraints
                }
            };
            const userMedia = await navigator.mediaDevices.getUserMedia(enumerateDevices);
            const localTracks = userMedia.getTracks();
            const devices = await navigator.mediaDevices.enumerateDevices();
            const mediaDevices = mediaDevicesUtils.getMediaDevices(devices, this.props.t);
            this.props.onEnumerateMediaDevices?.(mediaDevices);
            this.props.onEnumerateTracks?.(localTracks);
        } catch (error: any) {
            logger.warn({category: logger.category.WebRTC, message: 'cannot obtain camera & microphone access'}, error.message);
            const {code, message, dialogKey} = mediaDevicesUtils.getUserMediaErrorDetails(error);
            this.webRTCStartFailed({code, message, attempt: 1}, dialogKey);
            throw error;
        }
    };

    start = (retryParams: { retries: number, minTimeout: number } = {retries: 5, minTimeout: 2000}): Promise<any> =>
        this.getTokenWithRetries(retryParams)
            .then(({token}) => {
                this.setState({token});
            });

    getTokenWithRetries = (retryParams: { retries: number, minTimeout: number }) => {
        const {getTokenFn} = this.props;
        return promiseRetry((retry, attempt) => {
            if (attempt > 1) {
                logger.warn({category: logger.category.WebRTC, message: `get token attempt number: ${attempt}`});
            }
            return getTokenFn().catch(((error) => {
                this.webRTCStartFailed({
                    attempt,
                    code: error.name,
                    message: String(error)
                });
                return retry(error);
            }
            ));
        }, retryParams).catch((error) => {
            logger.warn({category: logger.category.WebRTC, message: 'cannot obtain token for room'}, error.message);
            this.webRTCStartFailed({
                attempt: retryParams.retries,
                code: error.name,
                message: String(error)
            }, String(error));
            throw error;
        });
    };

    webRTCStartFailed = (details: WebRTCConnectDetails, message?: string) => {
        if (message && !this.props.ignoreErrorDialog) {
            this.openErrorDialog(message);
        }
        this.setState({failedConnectDetails: details});
        this.props.onWebRTCStartFailed?.(details);
    };

    disconnectRoom = () => {
        this.state.room?.disconnect();
    };

    openErrorDialog = (message: string) => {
        this.setState({errorDialogProps: {open: true, message}});
    };

    closeErrorDialog = () => {
        this.setState({errorDialogProps: {open: false, message: ''}});
    };

    onRoomChange = (room: GroupRoomManager) => {
        this.setState({room});
    };

    onJoinRoomSuccess = () => {
        this.props.onWebRTCStartSuccess?.({attempt: (this.state.failedConnectDetails?.attempt || 0) + 1});
        this.setState({failedConnectDetails: null});
    };

    render() {
        const {children, t} = this.props;
        const {token, errorDialogProps} = this.state;
        const {closeErrorDialog, onRoomChange, onJoinRoomSuccess} = this;

        return (
            <Fragment>
                <KurentoRoomProvider
                    token={token}
                    onJoinRoomSuccess={onJoinRoomSuccess}
                    onRoomChange={onRoomChange}
                >
                    {({room}) => room ? children({room}) : <Loader className='tw-max-w-12 tw-max-h-12' />}
                </KurentoRoomProvider>
                <InformationModal
                    open={errorDialogProps.open}
                    fullWidth
                    maxWidth='sm'
                    title={t(T_KEYS.ROOM_ERROR_DIALOG.TITLE)}
                    onClose={closeErrorDialog}
                >
                    <span>{t(errorDialogProps.message)}</span>
                </InformationModal>
            </Fragment>
        );
    }
}

export default withTranslation()(KurentoRoom);
